import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  output,
  viewChildren,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isDefined, isNil, LinearUnit } from '@trimble-gcs/common';
import { ModusInputModule } from '@trimble-gcs/modus';
import { combineLatest, filter, map } from 'rxjs';
import { NotWhitespaceStringValidator } from '../../../utils/not-whitespace-string-validator';
import { Scan3dStyleUom } from '../../models/scan-3d-style-uom';
import { Scan3dUomLinearConverter } from '../scan-3d-uom-linear-converter';

@UntilDestroy()
@Component({
  selector: 'sd-scan-3d-length-input',
  imports: [CommonModule, ReactiveFormsModule, ModusInputModule],
  styles: `
    :host {
      display: flex;
      flex-direction: row;
      align-items: center;
      width: 100%;
      height: 100%;
    }
  `,
  templateUrl: './scan-3d-length-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Scan3dLengthInputComponent {
  private readonly inputs = viewChildren<ElementRef<HTMLInputElement>>('input');

  readonly scan3dStyleUom = input.required<Scan3dStyleUom>();
  readonly focus = input<boolean>();

  readonly lengthChanged = output<number | undefined>();
  readonly validationChanged = output();
  readonly focused = output<void>();
  readonly blurred = output<FocusEvent>();
  readonly enterPressed = output<void>();

  readonly showFtInInput = computed(() => this.scan3dStyleUom().lengthUnit === LinearUnit.FeetInch);
  readonly fraction = computed(() => this.scan3dStyleUom().lengthFractions);

  private internalLength: number | null = null;

  private readonly uomConverter = new Scan3dUomLinearConverter();
  private readonly numberPattern = /^-?(\d+(\.\d+)?|\.\d+)([eE][+-]?\d+)?$/; // Matches integers, decimals (including .[0-9]), negatives and scientific notation
  private readonly integerPattern = /^-?\d+$/; // Matches integers and negatives

  readonly form = new FormGroup({
    uom: new FormControl<Scan3dStyleUom | null>(null),
    length: new FormControl<number | null>(null, {
      validators: [
        Validators.required,
        Validators.pattern(this.numberPattern),
        NotWhitespaceStringValidator,
      ],
    }),
    feet: new FormControl<number | null>(null, {
      validators: [
        Validators.required,
        Validators.pattern(this.integerPattern),
        NotWhitespaceStringValidator,
      ],
    }),
    inches: new FormControl<number | null>(null, {
      validators: [
        Validators.required,
        Validators.pattern(this.integerPattern),
        Validators.min(0),
        Validators.max(11),
        NotWhitespaceStringValidator,
      ],
    }),
    fraction: new FormControl<number | null>(null),
  });

  constructor() {
    this.subscribeToUomChanges();
    this.subscribeToFormValueChanges();
    this.subscribeToFocusChanges();
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  setInternalLengthValue(length: number) {
    this.internalLength = length;
    this.resetForm();
  }

  getValidationErrors(): ValidationErrors | null {
    const errors = this.showFtInInput()
      ? this.getFtInValidationErrors()
      : this.form.controls.length.errors;

    return errors;
  }

  onFocus(): void {
    // Don't emit if focus was already set
    if (this.focus() === true) return;

    this.focused.emit();
  }

  onBlur($event: FocusEvent): void {
    this.trimFormValues();
    if (this.form.valid) this.formatFormValues();

    queueMicrotask(() => {
      this.blurred.emit($event);
    });
  }

  onEnter(): void {
    this.trimFormValues();
    if (this.form.valid) this.formatFormValues();

    queueMicrotask(() => {
      this.enterPressed.emit();
    });
  }

  private subscribeToFocusChanges() {
    const focus$ = toObservable(this.focus);
    const inputs$ = toObservable(this.inputs);

    combineLatest([focus$, inputs$])
      .pipe(untilDestroyed(this))
      .subscribe(([focus, inputs]) => {
        if (!focus || inputs.length === 0) return;

        inputs[0].nativeElement.focus();
      });
  }

  private trimFormValues() {
    this.form.setValue({
      uom: this.form.controls.uom.value,
      length: this.trimControlValue(this.form.controls.length.value),
      feet: this.trimControlValue(this.form.controls.feet.value),
      inches: this.trimControlValue(this.form.controls.inches.value),
      fraction: this.trimControlValue(this.form.controls.fraction.value),
    });
  }

  private formatFormValues() {
    const uom = this.scan3dStyleUom();
    const uomConverter = this.uomConverter;
    const length = this.internalLength;

    this.form.setValue(
      {
        uom,
        length: uomConverter.toUom(length ?? 0, uom),
        ...this.uomConverter.toFeetInchesFraction(length ?? 0, uom),
      },
      { emitEvent: false },
    );
  }

  private trimControlValue(value: number | null): number | null {
    if (isNil(value) || value.toString().trim() === '') return null;

    const numberValue = Number(value.toString().trim() ?? null);

    return isNaN(numberValue) ? value : numberValue;
  }

  private getFtInValidationErrors(): ValidationErrors | null {
    if (this.form.valid) return null;

    const errors = {
      feet: this.form.controls.feet.errors,
      inches: this.form.controls.inches.errors,
      fraction: this.form.controls.fraction.errors,
    };

    // Filter out keys with null or undefined values
    const filteredErrors = Object.fromEntries(
      Object.entries(errors).filter(([, value]) => isDefined(value)),
    ) as ValidationErrors;

    // Return null if no errors, else filtered errors
    return Object.keys(filteredErrors).length > 0 ? filteredErrors : null;
  }

  private resetForm() {
    const uom = this.scan3dStyleUom();
    const uomConverter = this.uomConverter;
    const length = this.internalLength;

    // Update form values
    this.form.reset(
      {
        uom,
        length: uomConverter.toUom(length ?? 0, uom),
        ...this.uomConverter.toFeetInchesFraction(length ?? 0, uom),
      },
      { emitEvent: false },
    );

    if (this.showFtInInput()) {
      // Update the fraction validator
      this.form.controls.fraction.setValidators([
        Validators.required,
        Validators.pattern(this.integerPattern),
        Validators.min(0),
        Validators.max(uom.lengthFractions - 1),
        NotWhitespaceStringValidator,
      ]);
      this.form.controls.length.disable();
      this.form.controls.feet.enable();
      this.form.controls.inches.enable();
      this.form.controls.fraction.enable();
    } else {
      this.form.controls.length.enable();
      this.form.controls.feet.disable();
      this.form.controls.inches.disable();
      this.form.controls.fraction.disable();
    }
  }

  private subscribeToUomChanges() {
    toObservable(this.scan3dStyleUom)
      .pipe(
        map(() => this.form.dirty),
        untilDestroyed(this),
      )
      .subscribe((formDirty) => {
        this.resetForm();

        if (formDirty) this.validationChanged.emit();
      });
  }

  private subscribeToFormValueChanges() {
    const form = this.form;
    form.valueChanges
      .pipe(
        filter(() => form.dirty),
        map(() => {
          const value = this.form.value;
          if (this.showFtInInput()) {
            return this.uomConverter.fromFeetInchesFraction(
              isDefined(value.feet) ? parseInt(value.feet.toString().trim()) : 0,
              isDefined(value.inches) ? parseInt(value.inches.toString().trim()) : 0,
              isDefined(value.fraction) ? parseInt(value.fraction.toString().trim()) : 0,
              this.scan3dStyleUom(),
            );
          } else {
            const uom = this.scan3dStyleUom();
            const length = Number(value.length?.toString().trim() ?? 0);
            const lengthFixed = Number(length.toFixed(uom.lengthDecimals)); // Trim decimals
            return this.uomConverter.toDefaultUom(lengthFixed, uom);
          }
        }),
        untilDestroyed(this),
      )
      .subscribe((lengthConverted) => {
        if (form.valid) {
          this.internalLength = lengthConverted;
          this.lengthChanged.emit(lengthConverted);
        } else {
          this.lengthChanged.emit(undefined);
        }
      });
  }
}
